import { isAfter, isBefore } from 'date-fns'
import { useField } from 'formik'

import { formatLocale } from '@/modules/shared/helpers/locale'
import { Input } from '../input'

import type { FocusEvent, FormEvent, InputHTMLAttributes } from 'react'
import type { PartiallyRequired } from '@/utils/typescript'

type DateInputProps = PartiallyRequired<InputHTMLAttributes<HTMLInputElement>, 'name'> & {
  noMargin?: boolean
}

let inputElement: HTMLInputElement | null

function setValidity(event: FormEvent<HTMLInputElement>) {
  inputElement = event.currentTarget
  inputElement.setCustomValidity('')
}

const DateInput = ({ value, ...props }: DateInputProps) => {
  const helpers = useField(props)[2]

  const handleDateBlur = (event: FocusEvent<HTMLInputElement>) => {
    const target = event.target.value

    if (!target) {
      helpers.setValue(undefined)
      return
    }

    const targetDate = new Date(target)
    let validatedValue = formatLocale(targetDate, 'yyyy-MM-dd')
    if (props.min) {
      const minDate = new Date(props.min)
      const min = formatLocale(minDate, 'yyyy-MM-dd')
      validatedValue = isBefore(targetDate, minDate) ? min : validatedValue
    }
    if (props.max) {
      const maxDate = new Date(props.max)
      const max = formatLocale(maxDate, 'yyyy-MM-dd')
      validatedValue = isAfter(targetDate, maxDate) ? max : validatedValue
    }

    helpers.setValue(validatedValue)
  }

  return (
    <Input
      type={'date'}
      {...props}
      value={typeof value === 'string' || typeof value === 'number' ? value : undefined}
      onInvalid={setValidity}
      onBlur={handleDateBlur}
    />
  )
}

export { DateInput }
